/**
 * Brightness stream effect.
 *
 * Draws the incoming camera frames onto an off-screen canvas with an adaptive brightness
 * filter applied, then streams the canvas back out. The boost is chosen per frame from the
 * measured scene luminance (Google Meet style "adjust brightness"), so dark environments are
 * lifted while well-lit ones are left untouched.
 */
import AdaptiveBrightness from './AdaptiveBrightness';
import { getBrightnessFilter } from './brightnessFilter';
export default class JitsiStreamBrightnessEffect {
    _adaptive;
    _inputVideoElement;
    _outputCanvasElement;
    _outputCanvasCtx;
    _rafHandle;
    _stream;
    /**
     * Creates a new brightness effect instance.
     */
    constructor() {
        this._adaptive = new AdaptiveBrightness();
        this._inputVideoElement = document.createElement('video');
        this._outputCanvasElement = document.createElement('canvas');
        this._outputCanvasCtx = this._outputCanvasElement.getContext('2d');
        this._rafHandle = 0;
        this._stream = null;
        this._render = this._render.bind(this);
    }
    /**
     * Checks if the local track supports this effect.
     *
     * @param {JitsiLocalTrack} jitsiLocalTrack - Track to apply effect.
     * @returns {boolean} True if this effect can run on this track.
     */
    isEnabled(jitsiLocalTrack) {
        return jitsiLocalTrack.isVideoTrack() && jitsiLocalTrack.videoType === 'camera';
    }
    /**
     * Starts the effect on the given camera stream.
     *
     * @param {MediaStream} stream - Stream to be used for processing.
     * @returns {MediaStream} The stream with the applied effect.
     */
    startEffect(stream) {
        this._stream = stream;
        this._adaptive.reset();
        const firstVideoTrack = stream.getVideoTracks()[0];
        const { height, frameRate, width } = firstVideoTrack.getSettings ? firstVideoTrack.getSettings() : firstVideoTrack.getConstraints();
        this._outputCanvasElement.width = parseInt(String(width), 10);
        this._outputCanvasElement.height = parseInt(String(height), 10);
        this._inputVideoElement.width = parseInt(String(width), 10);
        this._inputVideoElement.height = parseInt(String(height), 10);
        this._inputVideoElement.autoplay = true;
        this._inputVideoElement.srcObject = stream;
        // autoplay is unreliable for out-of-DOM elements on srcObject reassignment.
        this._inputVideoElement.play().catch(() => undefined);
        this._rafHandle = requestAnimationFrame(this._render);
        return this._outputCanvasElement.captureStream(parseInt(String(frameRate), 10));
    }
    /**
     * Stops the effect and releases the input stream reference.
     *
     * @returns {void}
     */
    stopEffect() {
        cancelAnimationFrame(this._rafHandle);
        this._rafHandle = 0;
        this._inputVideoElement.srcObject = null;
        this._stream = null;
        this._adaptive.reset();
    }
    /**
     * Renders a single frame onto the output canvas with the brightness filter applied
     * and schedules the next frame.
     *
     * @private
     * @returns {void}
     */
    _render() {
        if (this._outputCanvasCtx && this._inputVideoElement.readyState >= 2) {
            const level = this._adaptive.update(this._inputVideoElement, this._outputCanvasElement.width, this._outputCanvasElement.height);
            this._outputCanvasCtx.filter = getBrightnessFilter(level);
            this._outputCanvasCtx.drawImage(this._inputVideoElement, 0, 0, this._outputCanvasElement.width, this._outputCanvasElement.height);
        }
        this._rafHandle = requestAnimationFrame(this._render);
    }
}
